import useFetch from "hooks/useFetch";
import { useTranslation } from "react-i18next";
import { parseStrapiFormat } from "common/utils/strapi";
import type { Plan } from "types";

type Option = { value: number; label: string };

const postExecute = (response: any) => parseStrapiFormat(response);

type Props = {
  value?: number[];
  onChange?: (value: number[]) => void;
  filters?: { name: string; value: any }[];
};

function PlanCheckboxList({ value, onChange, filters = [] }: Props) {
  const { t } = useTranslation();

  const params: Record<string, any> = { "pagination[limit]": 5000 };
  filters.forEach((f) => {
    params[`filters[${f.name}]`] = f.value;
  });

  const queryString = Object.keys(params)
    .filter((key) => params[key] != null)
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`,
    )
    .join("&");

  const url = `plans?${queryString}`;

  const { data, isFetching }: any = useFetch(url, { postExecute });

  const options: Option[] = data
    ? (data as Plan[]).map(({ id, name }) => ({ value: id, label: name }))
    : [];

  const selected = value ?? [];

  const handleToggle = (id: number) => {
    const next = selected.includes(id)
      ? selected.filter((v) => v !== id)
      : [...selected, id];
    onChange?.(next);
  };

  const handleSelectAll = () => onChange?.(options.map((o) => o.value));
  const handleClear = () => onChange?.([]);

  if (isFetching) {
    return (
      <span className="text-sm text-slate-500">{t("common.loading")}</span>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={handleSelectAll}
          className="text-sm px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 text-slate-600"
        >
          {t("common.selectAll")}
        </button>
        <button
          type="button"
          onClick={handleClear}
          className="text-sm px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 text-slate-600"
        >
          {t("common.clearSelection")}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {options.map((opt) => (
          <label
            key={opt.value}
            className="flex items-center gap-2 cursor-pointer text-base text-slate-700 hover:text-slate-900"
          >
            <input
              type="checkbox"
              className="accent-primary rounded"
              checked={selected.includes(opt.value)}
              onChange={() => handleToggle(opt.value)}
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}

export default PlanCheckboxList;
